import { FC } from 'react';
import { Paperclip, Pencil } from 'lucide-react';

import { useGetDegreesQuery } from '@teachur/http-client';
import { Button } from '@new/common/ui/Button/Button';
import { SITUATION_OPTIONS, TARGET_DEGREE_OPTIONS } from '../../utils/setupOptions';
import type { Evaluation } from '@new/features/transcripts/shared/types';

interface InfoSummaryPanelProps {
  evaluation: Evaluation;
  canEdit: boolean;
  onEdit: () => void;
}

const Row: FC<{ label: string; value?: string | null }> = ({ label, value }) =>
  !value ? null : (
    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
      <dt className="[font-size:var(--fs-b4)]! font-medium text-(--neutral-black-NB1000)!">
        {label}
      </dt>
      <dd className="mt-1 [font-size:var(--fs-b4)]! text-(--neutral-black-NB700)! sm:col-span-2 sm:mt-0">
        {value}
      </dd>
    </div>
  );

export const InfoSummaryPanel: FC<InfoSummaryPanelProps> = ({ evaluation, canEdit, onEdit }) => {
  const situation = SITUATION_OPTIONS.find((o) => o.value === evaluation.priorEducationStatus);
  const targetDegreeLabel = TARGET_DEGREE_OPTIONS.find(
    (o) => o.value === evaluation.targetDegreeType
  )?.label;
  const { data: degrees = [] } = useGetDegreesQuery();
  const targetProgramName =
    degrees.find((d) => d.id === evaluation.targetDegreeId)?.name ?? evaluation.targetProgram;

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <h3 className="m-0! [font-size:var(--fs-h6)]! font-bold text-(--neutral-black-NB1000)!">
          Your information
        </h3>
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Pencil className="size-4" />}
            onClick={onEdit}
            text="Edit info"
          />
        ) : null}
      </div>

      <dl className="mt-4 divide-y divide-(--neutral-black-NB100)">
        <Row label="Situation" value={situation?.title ?? null} />
        <Row label="Target degree" value={targetDegreeLabel} />
        <Row label="Program you're interested in" value={targetProgramName} />
        <Row label="Degree" value={evaluation.degreeName} />
        <Row label="Institution" value={evaluation.degreeInstitution} />
        <Row label="Former name" value={evaluation.formerName} />

        {evaluation.nameChangeDocumentUrl ? (
          <div className="px-4 py-3 sm:px-0">
            <dt className="[font-size:var(--fs-b4)]! font-medium text-(--neutral-black-NB1000)!">
              Name-change document
            </dt>
            <dd className="mt-2">
              <a
                href={evaluation.nameChangeDocumentUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-(--primary-O900)! underline"
              >
                <Paperclip className="size-4" />
                Preview
              </a>
            </dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
};
